/* global theme */
if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      try {
        const dataEl = this.querySelector('[data-bundle-data]');
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.data = { title: '', components: [] };
      }
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.bundleTitle = this.dataset.bundleTitle || this.data.title || 'Bundle';
      this.bundleKey = this.dataset.bundleKey || '';
      this.discountPct = parseFloat(this.dataset.discountPct) || 0;
      this.componentEls = Array.from(this.querySelectorAll('[data-component-index]'));
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.compareEl = this.querySelector('[data-bundle-compare]');
      this.savingsEl = this.querySelector('[data-bundle-savings]');
      this.addBtn = this.querySelector('[data-bundle-add]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.state = [];
      this.init();
    }

    init() {
      this.componentEls.forEach((el, i) => {
        const comp = this.data.components[i];
        if (!comp) return;
        this.setupComponent(el, comp, i);
      });
      if (this.addBtn) this.addBtn.addEventListener('click', this.addToCart.bind(this));
      this.recalculate();
    }

    setupComponent(el, comp, index) {
      const modelWrap = el.querySelector('[data-model-wrap]');
      const modelSel = el.querySelector('[data-model]');
      const modelName = el.querySelector('[data-model-name]');
      const sizeWrap = el.querySelector('[data-size-wrap]');
      const sizeSel = el.querySelector('[data-size]');
      const products = comp.products || [];

      this.state[index] = {
        comp,
        products,
        productIndex: 0,
        variantId: null,
        price: 0,
        available: false,
        applicable: true,
        currentProductId: null,
        showWhen: comp.show_when || [],
        els: { modelWrap, modelName, sizeWrap, sizeSel, el }
      };

      // Model selector only when there is more than one product option.
      if (products.length > 1) {
        modelSel.innerHTML = products
          .map((p, pi) => `<option value="${pi}">${this.escape(p.title)}</option>`)
          .join('');
        modelWrap.hidden = false;
        if (modelName) modelName.hidden = true;
        modelSel.addEventListener('change', () => {
          this.populateSizes(index, products[modelSel.value]);
          this.recalculate();
        });
      } else {
        modelWrap.hidden = true;
        // Single fixed model: show its name as static text.
        if (modelName && products[0]) {
          modelName.textContent = products[0].title;
          modelName.hidden = false;
        }
      }

      sizeSel.addEventListener('change', () => {
        this.commitSize(index, sizeSel);
        this.recalculate();
      });

      if (products[0]) this.populateSizes(index, products[0]);
    }

    populateSizes(index, product) {
      const st = this.state[index];
      const { sizeWrap, sizeSel } = st.els;
      const variants = (product && product.variants) || [];
      const isSingle =
        variants.length === 1 &&
        (variants[0].title === 'Default Title' || variants[0].title === 'Default');

      sizeSel.innerHTML = variants
        .map((v) => {
          let suffix = '';
          if (!v.available) suffix = ' — out of stock';
          else if (v.backorder) suffix = ' (backorder)';
          return (
            `<option value="${v.id}" data-price="${v.price}" data-compare="${v.compare_at || ''}" ${v.available ? '' : 'disabled'}>` +
            `${this.escape(v.title)}${suffix} · ${this.formatMoney(v.price)}` +
            `</option>`
          );
        })
        .join('');

      sizeWrap.hidden = isSingle;

      const firstAvailable = variants.find((v) => v.available) || variants[0];
      if (firstAvailable) sizeSel.value = firstAvailable.id;

      const productIndex = st.products.indexOf(product);
      st.productIndex = productIndex < 0 ? 0 : productIndex;
      st.currentProductId = product ? product.id : null;

      this.updateThumb(index, product);
      this.updateSelectsLayout(index);
      this.commitSize(index, sizeSel);
    }

    updateThumb(index, product) {
      const img = this.state[index].els.el.querySelector('[data-comp-image]');
      if (!img) return;
      if (product && product.image) {
        img.src = product.image;
        img.alt = product.title || '';
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }

    updateSelectsLayout(index) {
      const { modelWrap, sizeWrap } = this.state[index].els;
      const wrap = modelWrap.parentElement;
      const two = !modelWrap.hidden && !sizeWrap.hidden;
      if (wrap) wrap.classList.toggle('has-two', two);
    }

    commitSize(index, sizeSel) {
      const opt = sizeSel.selectedOptions[0];
      const st = this.state[index];
      if (opt) {
        st.variantId = parseInt(sizeSel.value, 10);
        st.price = parseInt(opt.dataset.price, 10) || 0;
        st.compareAt = parseInt(opt.dataset.compare, 10) || 0;
        st.available = !opt.disabled;
      } else {
        st.variantId = null;
        st.price = 0;
        st.compareAt = 0;
        st.available = false;
      }
    }

    updateVisibility() {
      const selectedIds = this.state
        .filter(Boolean)
        .map((st) => st.currentProductId)
        .filter(Boolean);
      this.state.forEach((st, i) => {
        if (!st) return;
        st.applicable =
          !st.showWhen || st.showWhen.length === 0
            ? true
            : st.showWhen.some((id) => selectedIds.includes(id));
        this.componentEls[i].hidden = !st.applicable;
      });
    }

    recalculate() {
      this.updateVisibility();
      let subtotal = 0; // sum of current (sale) prices
      let compareTotal = 0; // sum of originals (compare-at where a product is on sale)
      let allAvailable = true;
      this.state.forEach((st, i) => {
        if (!st) return;
        const el = this.componentEls[i];
        const priceEl = el.querySelector('[data-comp-price]');
        if (!st.applicable) {
          if (priceEl) priceEl.textContent = '';
          el.classList.remove('is-unavailable');
          return;
        }
        const onSale = st.compareAt && st.compareAt > st.price;
        subtotal += st.price || 0;
        compareTotal += onSale ? st.compareAt : st.price || 0;
        if (priceEl) {
          if (!st.price) {
            priceEl.textContent = '';
          } else if (onSale) {
            priceEl.innerHTML = `<s>${this.formatMoney(st.compareAt)}</s> ${this.formatMoney(st.price)}`;
          } else {
            priceEl.textContent = this.formatMoney(st.price);
          }
        }
        const ok = st.variantId && st.available;
        if (!ok) allAvailable = false;
        el.classList.toggle('is-unavailable', st.variantId && !st.available);
      });

      // What the customer pays: sale prices minus the bundle discount.
      const final =
        this.discountPct > 0 ? Math.round(subtotal * (1 - this.discountPct / 100)) : subtotal;
      // The struck-through reference includes each product's own sale + the bundle discount.
      const showCompare = compareTotal > final && final > 0;

      if (this.totalEl) this.totalEl.innerHTML = this.formatMoney(final);
      if (this.compareEl) {
        if (showCompare) {
          this.compareEl.innerHTML = this.formatMoney(compareTotal);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
      if (this.savingsEl) {
        if (showCompare) {
          const savings = compareTotal - final;
          const effPct = Math.round((savings / compareTotal) * 100);
          this.savingsEl.textContent = `You save ${this.formatMoney(savings)} (${effPct}% off)`;
          this.savingsEl.hidden = false;
        } else {
          this.savingsEl.hidden = true;
        }
      }

      if (this.addBtn) this.addBtn.setAttribute('aria-disabled', allAvailable ? 'false' : 'true');
      this.allAvailable = allAvailable;
    }

    async addToCart() {
      if (this.addBtn.getAttribute('aria-disabled') === 'true') {
        this.showError('One of the selected options is out of stock. Please change it to continue.');
        return;
      }
      this.showError('');
      this.addBtn.classList.add('is-loading');
      this.addBtn.setAttribute('aria-disabled', 'true');

      const bundleId = `${this.slug(this.bundleTitle)}-${Date.now()}`;
      const items = this.state
        .filter((st) => st && st.applicable && st.variantId)
        .map((st) => ({
          id: st.variantId,
          quantity: 1,
          properties: {
            Bundle: this.bundleTitle,
            _bundle_id: bundleId,
            _bundle_key: this.bundleKey
          }
        }));

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const addRes = await fetch(`${theme.routes.cartAdd}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });
        const addData = await addRes.json();
        if (!addRes.ok) {
          throw new Error(addData.description || addData.message || 'Error adding to cart');
        }

        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          const names = cartDrawer.getSectionsToRender().map((s) => s.section);
          const secRes = await fetch(`?sections=${names.join(',')}`);
          const sections = await secRes.json();
          cartDrawer.renderContents({ sections }, true);
        } else {
          window.location.href = theme.routes.cart;
        }
      } catch (err) {
        this.showError(err.message || 'Could not add the bundle.');
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.recalculate();
      }
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
    }

    slug(str) {
      return String(str)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    escape(str) {
      const div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }

    formatMoney(cents) {
      const value = (typeof cents === 'string' ? cents.replace(/[^0-9-]/g, '') : cents) || 0;
      const format = this.moneyFormat;
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;

      function defaultTo(num, precision) {
        return typeof num === 'undefined' || num === null || isNaN(num) ? precision : num;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ',');
        decimal = defaultTo(decimal, '.');
        if (isNaN(number) || number == null) return 0;
        number = (number / 100.0).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const cents = parts[1] ? decimal + parts[1] : '';
        return dollars + cents;
      }

      const match = format.match(placeholder);
      const token = match ? match[1] : 'amount';
      let out;
      switch (token) {
        case 'amount':
          out = formatWithDelimiters(value, 2);
          break;
        case 'amount_no_decimals':
          out = formatWithDelimiters(value, 0);
          break;
        case 'amount_with_comma_separator':
          out = formatWithDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          out = formatWithDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          out = formatWithDelimiters(value, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          out = formatWithDelimiters(value, 0, ' ', '');
          break;
        default:
          out = formatWithDelimiters(value, 2);
      }
      return format.replace(placeholder, out);
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
